import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Trash2, UserPlus, User, Upload, Edit2, X, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTeamAgents, TeamAgent } from "@/hooks/useTeamAgents";
import { logger } from '@/lib/logger';
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TeamUser {
  user_id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

export function TeamRoster() {
  const { agents, isLoading, invalidate } = useTeamAgents();
  const { getEffectiveUserId } = useImpersonation();
  const { toast } = useToast();
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addFromMembersOpen, setAddFromMembersOpen] = useState(false);
  const [addNewAgentOpen, setAddNewAgentOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<TeamAgent | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<TeamAgent | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [addingAgent, setAddingAgent] = useState(false);
  
  // Form states
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [agentName, setAgentName] = useState("");
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [headshotPreview, setHeadshotPreview] = useState<string | null>(null);

  useEffect(() => {
    if (addFromMembersOpen) {
      loadTeamUsers();
    }
  }, [addFromMembersOpen]);

  const loadTeamUsers = async () => {
    setLoadingUsers(true);
    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      // Get user's team
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', effectiveUserId)
        .single();

      if (!profile?.team_id) return;

      // Get all team users
      const { data: users, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, avatar_url')
        .eq('team_id', profile.team_id);

      if (error) throw error;

      // Filter out users already in roster
      const agentsWithUsers = agents.filter(a => a.user_id).map(a => a.user_id);
      const availableUsers = (users || []).filter(
        u => !agentsWithUsers.includes(u.user_id)
      );

      setTeamUsers(availableUsers);
    } catch (error: any) {
      logger.error('Error loading team users:', error);
      toast({
        title: "Error",
        description: "Failed to load team members",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddFromMember = async () => {
    if (!selectedUserId) {
      toast({
        title: "Error",
        description: "Please select a team member",
        variant: "destructive",
      });
      return;
    }

    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      // Get user's team
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', effectiveUserId)
        .single();

      if (!profile?.team_id) throw new Error("No team found");

      // Get selected user's profile
      const selectedUser = teamUsers.find(u => u.user_id === selectedUserId);
      if (!selectedUser) throw new Error("Selected user not found");

      // Create team agent entry
      const { error } = await supabase
        .from('team_agents')
        .insert({
          team_id: profile.team_id,
          user_id: selectedUserId,
          name: selectedUser.full_name || selectedUser.email || "Unknown",
          headshot_url: selectedUser.avatar_url,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Agent added to roster",
      });

      setAddFromMembersOpen(false);
      setSelectedUserId("");
      invalidate();
    } catch (error: any) {
      logger.error('Error adding agent from member:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add agent",
        variant: "destructive",
      });
    }
  };

  const handleAddNewAgent = async () => {
    if (!agentName.trim()) {
      toast({
        title: "Error",
        description: "Please enter agent name",
        variant: "destructive",
      });
      return;
    }

    setAddingAgent(true);
    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) {
        toast({
          title: "Error",
          description: "Unable to authenticate. Please refresh the page and try again.",
          variant: "destructive",
        });
        setAddingAgent(false);
        return;
      }

      // Get user's team
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', effectiveUserId)
        .single();

      if (!profile?.team_id) throw new Error("No team found");

      // Create team agent entry first (without headshot)
      const { data: newAgent, error: insertError } = await supabase
        .from('team_agents')
        .insert({
          team_id: profile.team_id,
          user_id: null,
          name: agentName.trim(),
          headshot_url: null,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      if (!newAgent) throw new Error("Failed to create agent");

      // Upload headshot if provided (now we have the agent ID)
      let headshotUrl: string | null = null;
      if (headshotFile && newAgent.id) {
        try {
          headshotUrl = await uploadHeadshot(headshotFile, newAgent.id);
          
          // Update agent with headshot URL
          const { error: updateError } = await supabase
            .from('team_agents')
            .update({ headshot_url: headshotUrl })
            .eq('id', newAgent.id);

          if (updateError) {
            logger.error('Error updating agent with headshot URL:', updateError);
            // Don't fail the whole operation if headshot update fails
          }
        } catch (uploadError: any) {
          logger.error('Error uploading headshot:', uploadError);
          // Don't fail the whole operation if headshot upload fails
          toast({
            title: "Warning",
            description: "Agent created but headshot upload failed: " + (uploadError.message || "Unknown error"),
            variant: "default",
          });
        }
      }

      toast({
        title: "Success",
        description: "Agent added to roster",
      });

      setAddNewAgentOpen(false);
      setAgentName("");
      setHeadshotFile(null);
      setHeadshotPreview(null);
      invalidate();
    } catch (error: any) {
      logger.error('Error adding new agent:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add agent",
        variant: "destructive",
      });
    } finally {
      setAddingAgent(false);
    }
  };

  const handleEditAgent = async () => {
    if (!editingAgent || !agentName.trim()) {
      toast({
        title: "Error",
        description: "Please enter agent name",
        variant: "destructive",
      });
      return;
    }

    try {
      let headshotUrl = editingAgent.headshot_url;

      // Upload new headshot if provided
      if (headshotFile) {
        headshotUrl = await uploadHeadshot(headshotFile, editingAgent.id);
      }

      // Update team agent
      const { error } = await supabase
        .from('team_agents')
        .update({
          name: agentName.trim(),
          headshot_url: headshotUrl,
        })
        .eq('id', editingAgent.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Agent updated",
      });

      setEditingAgent(null);
      setAgentName("");
      setHeadshotFile(null);
      setHeadshotPreview(null);
      invalidate();
    } catch (error: any) {
      logger.error('Error updating agent:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update agent",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAgent = async () => {
    if (!deletingAgent) return;

    try {
      // Delete headshot from storage if exists
      if (deletingAgent.headshot_url) {
        try {
          const urlParts = deletingAgent.headshot_url.split('/');
          const fileName = urlParts[urlParts.length - 1];
          const filePath = `team-agents/${deletingAgent.team_id}/${deletingAgent.id}/${fileName}`;
          
          await supabase.storage
            .from('logos')
            .remove([filePath]);
        } catch (storageError) {
          // Log but don't fail if storage delete fails
          logger.error('Error deleting headshot from storage:', storageError);
        }
      }

      const { error } = await supabase
        .from('team_agents')
        .delete()
        .eq('id', deletingAgent.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Agent removed from roster",
      });

      setDeletingAgent(null);
      invalidate();
    } catch (error: any) {
      logger.error('Error deleting agent:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete agent",
        variant: "destructive",
      });
    }
  };

  const uploadHeadshot = async (file: File, agentId: string | null): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        const effectiveUserId = await getEffectiveUserId();
        if (!effectiveUserId) throw new Error("Not authenticated");

        // Get user's team
        const { data: profile } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('user_id', effectiveUserId)
          .single();

        if (!profile?.team_id) throw new Error("No team found");

        const fileExt = file.name.split(".").pop();
        const fileName = `headshot-${Date.now()}.${fileExt}`;
        const filePath = `team-agents/${profile.team_id}/${agentId || 'new'}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("logos")
          .upload(filePath, file, {
            contentType: file.type,
            upsert: true,
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("logos")
          .getPublicUrl(filePath);

        resolve(publicUrl);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleHeadshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setHeadshotFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setHeadshotPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const startEdit = (agent: TeamAgent) => {
    setEditingAgent(agent);
    setAgentName(agent.name);
    setHeadshotPreview(agent.headshot_url);
    setHeadshotFile(null);
  };

  const cancelEdit = () => {
    setEditingAgent(null);
    setAgentName("");
    setHeadshotFile(null);
    setHeadshotPreview(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Team Roster</CardTitle>
            <CardDescription>
              Manage listing agents available for selection in North reports
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={addFromMembersOpen} onOpenChange={setAddFromMembersOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add from Team Members
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Agent from Team Members</DialogTitle>
                  <DialogDescription>
                    Select a team member to add to the roster
                  </DialogDescription>
                </DialogHeader>
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : teamUsers.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    All team members are already in the roster
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {teamUsers.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => setSelectedUserId(user.user_id)}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          selectedUserId === user.user_id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback>
                              {user.full_name?.[0] || user.email?.[0] || "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="font-medium">
                              {user.full_name || user.email || "Unknown"}
                            </div>
                            {user.full_name && (
                              <div className="text-sm text-muted-foreground">
                                {user.email}
                              </div>
                            )}
                          </div>
                          {selectedUserId === user.user_id && (
                            <Check className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddFromMembersOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddFromMember} disabled={!selectedUserId}>
                    Add to Roster
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setAddNewAgentOpen(true)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add New Agent
            </Button>
            <Dialog open={addNewAgentOpen} onOpenChange={setAddNewAgentOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Agent</DialogTitle>
                  <DialogDescription>
                    Create a standalone agent entry (for agents without logins)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="agent-name">Agent Name</Label>
                    <Input
                      id="agent-name"
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      placeholder="Enter agent name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Headshot (Optional)</Label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-full border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
                        {headshotPreview ? (
                          <img
                            src={headshotPreview}
                            alt="Headshot preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleHeadshotChange}
                          className="hidden"
                          id="headshot-upload"
                        />
                        <Label htmlFor="headshot-upload">
                          <Button variant="outline" size="sm" asChild>
                            <span className="cursor-pointer">
                              <Upload className="h-4 w-4 mr-2" />
                              Upload Headshot
                            </span>
                          </Button>
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setAddNewAgentOpen(false);
                    setAgentName("");
                    setHeadshotFile(null);
                    setHeadshotPreview(null);
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddNewAgent} disabled={!agentName.trim() || addingAgent}>
                    {addingAgent ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Add Agent
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {agents.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No agents in roster yet</p>
            <p className="text-sm mt-2">Add agents from team members or create new entries</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Norths</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  {editingAgent?.id === agent.id ? (
                    <>
                      <TableCell colSpan={3}>
                        <div className="space-y-4 py-2">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full border-2 border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                              {headshotPreview ? (
                                <img
                                  src={headshotPreview}
                                  alt="Headshot"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <User className="w-8 h-8 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 space-y-2">
                              <Input
                                value={agentName}
                                onChange={(e) => setAgentName(e.target.value)}
                                placeholder="Agent name"
                                className="text-base"
                              />
                              <div>
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleHeadshotChange}
                                  className="hidden"
                                  id={`edit-headshot-${agent.id}`}
                                />
                                <Label htmlFor={`edit-headshot-${agent.id}`}>
                                  <Button variant="outline" size="sm" asChild>
                                    <span className="cursor-pointer">
                                      <Upload className="h-4 w-4 mr-2" />
                                      Change Photo
                                    </span>
                                  </Button>
                                </Label>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleEditAgent}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEdit}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={agent.headshot_url || undefined} />
                            <AvatarFallback className="bg-primary text-primary-foreground">
                              {agent.name[0]?.toUpperCase() || "A"}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{agent.name}</span>
                            {!agent.is_user_linked && (
                              <Badge variant="outline" className="text-xs">
                                Standalone
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {agent.norths_count.total > 0 ? (
                          <div className="flex flex-col gap-1">
                            <div className="text-sm">
                              <span className="font-medium">{agent.norths_count.total}</span>
                              <span className="text-muted-foreground"> total</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {agent.norths_count.published} published, {agent.norths_count.unpublished} unpublished
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(agent)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeletingAgent(agent)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <AlertDialog open={!!deletingAgent} onOpenChange={(open) => !open && setDeletingAgent(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Agent from Roster?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove {deletingAgent?.name} from the team roster. This action cannot be undone.
                Existing North reports using this agent will not be affected.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteAgent} className="bg-destructive text-destructive-foreground">
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

